import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon, MinusIcon, UsersIcon } from '@/components/icons';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import type { AddOnResponse, SelectedAddOn } from '@/features/cart/types';


// Helper component for price display
const PriceDisplay: React.FC<{ price: number; currency?: string }> = ({ price, currency }) => {
  // Format price using currency if provided, otherwise show raw amount
  const formattedPrice = currency 
    ? formatCurrencyForDisplay(price, currency) 
    : `${(price / 100).toFixed(2)}`;
  return <p className="text-xs text-gray-400">{formattedPrice}</p>;
};

interface EditGuestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  allGuests: number; // Total number of guests
  editAddOnsPerGuest: SelectedAddOn[][]; // Current addons per guest
  addOns: AddOnResponse[]; // All available addons for lookup
  removedGuests: Set<number>; // Currently removed guest indices
  removedItems: Set<string>; // Currently removed addon items (format: "guestIndex-addOnId")
  onRemoveGuest: (guestIndex: number) => void;
  onRestoreGuest: (guestIndex: number) => void;
  getAddOnById: (addOnId: string) => AddOnResponse | undefined;
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
}

interface GuestData {
  guestIndex: number;
  guestNum: number;
  isRemoved: boolean;
  guestAddOns: SelectedAddOn[];
  activeAddOns: SelectedAddOn[];
  removedAddOns: SelectedAddOn[];
  totalCount: number;
}

const EditGuestsModal: React.FC<EditGuestsModalProps> = ({
  isOpen,
  onClose,
  allGuests,
  editAddOnsPerGuest,
  removedGuests,
  removedItems,
  onRemoveGuest,
  onRestoreGuest,
  getAddOnById
}) => {
  const [expandedGuestIndex, setExpandedGuestIndex] = useState<number | null>(null);
  const [pendingActions, setPendingActions] = useState<Set<number>>(new Set());

  // Reset modal state when it opens/closes or when edit state changes
  React.useEffect(() => {
    if (isOpen) {
      // Reset local state when modal opens
      setExpandedGuestIndex(null);
      setPendingActions(new Set());
    }
  }, [isOpen, removedGuests, removedItems]);

  // Compute guest data once per render
  const guestsData = useMemo<GuestData[]>(() => {
    return Array.from({ length: allGuests }, (_, i) => {
      const guestIndex = i;
      const guestNum = i + 1;
      const isRemoved = removedGuests.has(guestIndex);
      const guestAddOns = editAddOnsPerGuest[guestIndex] || [];
      
      // Separate active and removed addons
      const activeAddOns: SelectedAddOn[] = [];
      const removedAddOns: SelectedAddOn[] = [];
      
      guestAddOns.forEach(addOn => {
        const key = `${guestIndex}-${addOn.add_on_id}`;
        if (removedItems.has(key)) {
          removedAddOns.push(addOn);
        } else {
          activeAddOns.push(addOn);
        }
      });
      
      return {
        guestIndex,
        guestNum,
        isRemoved,
        guestAddOns,
        activeAddOns,
        removedAddOns,
        totalCount: guestAddOns.length
      };
    });
  }, [allGuests, editAddOnsPerGuest, removedGuests, removedItems]);

  // Handle remove guest with optimistic update
  const handleRemoveGuest = useCallback((guestIndex: number) => {
    // Prevent removing if already pending or removed
    if (pendingActions.has(guestIndex) || removedGuests.has(guestIndex)) {
      return;
    }

    // Optimistic UI update
    setPendingActions(prev => new Set(prev).add(guestIndex));
    
    // Close expanded section if this guest is expanded
    if (expandedGuestIndex === guestIndex) {
      setExpandedGuestIndex(null);
    }

    // Call parent handler
    onRemoveGuest(guestIndex);

    // Clear pending state after a short delay to allow parent state to update
    setTimeout(() => {
      setPendingActions(prev => {
        const next = new Set(prev);
        next.delete(guestIndex);
        return next;
      });
    }, 300);
  }, [pendingActions, removedGuests, expandedGuestIndex, onRemoveGuest]);

  // Handle restore guest with optimistic update
  const handleRestoreGuest = useCallback((guestIndex: number) => {
    // Check if guest is actually removed (in actual state or pending removal)
    const isActuallyRemoved = removedGuests.has(guestIndex);
    
    // Prevent restoring if already pending restore action or not removed
    if (pendingActions.has(guestIndex) || !isActuallyRemoved) {
      return;
    }

    // Optimistic UI update - remove from pending immediately for visual feedback
    setPendingActions(prev => {
      const next = new Set(prev);
      next.add(guestIndex);
      return next;
    });

    // Call parent handler
    onRestoreGuest(guestIndex);

    // Clear pending state after a short delay to allow parent state to update
    setTimeout(() => {
      setPendingActions(prev => {
        const next = new Set(prev);
        next.delete(guestIndex);
        return next;
      });
    }, 300);
  }, [pendingActions, removedGuests, onRestoreGuest]);

  // Toggle expanded section
  const handleToggleExpand = useCallback((guestIndex: number) => {
    setExpandedGuestIndex(prev => prev === guestIndex ? null : guestIndex);
  }, []);

  // Close expanded section when guest is removed
  React.useEffect(() => {
    if (expandedGuestIndex !== null && removedGuests.has(expandedGuestIndex)) {
      setExpandedGuestIndex(null);
    }
  }, [removedGuests, expandedGuestIndex]);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md max-h-[90vh] bg-gray-900 rounded-2xl border border-gray-700/50 overflow-hidden shadow-2xl flex flex-col"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-full text-gray-300 hover:text-white transition-all duration-200 backdrop-blur-sm"
            >
              <XIcon className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="relative p-6 border-b border-gray-700/50 flex-shrink-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                  <UsersIcon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Manage Guests</h3>
                  <p className="text-sm text-gray-400">
                    {allGuests} {allGuests === 1 ? 'Guest' : 'Guests'}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Guests Section */}
              <div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  All Guests
                </h4>
                
                <div className="space-y-2">
                  {guestsData.map((guestData) => {
                    const { 
                      guestIndex, 
                      guestNum, 
                      isRemoved, 
                      activeAddOns, 
                      removedAddOns, 
                      totalCount 
                    } = guestData;
                    
                    const isPending = pendingActions.has(guestIndex);
                    const isExpanded = expandedGuestIndex === guestIndex;
                    const hasAddOns = totalCount > 0;
                    
                    return (
                      <div key={guestIndex} className="space-y-2">
                        <div
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isRemoved || isPending
                              ? 'bg-gray-800/20 border-red-500/30 opacity-50'
                              : 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800/70'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                              isRemoved || isPending
                                ? 'bg-red-500/20 border-red-500/30'
                                : 'bg-blue-500/20 border-blue-500/30'
                            }`}>
                              <span className={`font-semibold text-sm transition-colors ${
                                isRemoved || isPending
                                  ? 'text-red-400'
                                  : 'text-blue-400'
                              }`}>
                                {guestNum}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className={`font-medium transition-all ${
                                isRemoved || isPending
                                  ? 'text-red-300 line-through'
                                  : 'text-white'
                              }`}>
                                Guest {guestNum}
                              </span>
                              
                              {hasAddOns && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleExpand(guestIndex);
                                  }}
                                  disabled={isRemoved || isPending}
                                  className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs font-semibold border border-blue-500/30 hover:bg-blue-500/30 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Click to view add-ons"
                                >
                                  <span>
                                    {totalCount} {totalCount === 1 ? 'add-on' : 'add-ons'}
                                    {removedAddOns.length > 0 && ` (${removedAddOns.length} removed)`}
                                  </span>
                                  <svg 
                                    className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              )}
                              
                              {!hasAddOns && (
                                <span className="text-xs text-gray-500">(No add-ons)</span>
                              )}
                              
                              {(isRemoved || isPending) && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs font-semibold border border-red-500/30">
                                  {isPending ? 'Removing...' : 'Removed'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {(isRemoved || isPending) && (
                              <button
                                onClick={() => handleRestoreGuest(guestIndex)}
                                disabled={isPending}
                                className="p-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Restore guest"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                              </button>
                            )}
                            
                            {!isRemoved && !isPending && (
                              <button
                                onClick={() => handleRemoveGuest(guestIndex)}
                                className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-all active:scale-95"
                                title="Remove guest"
                              >
                                <MinusIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Addons Dropdown */}
                        {isExpanded && hasAddOns && !isRemoved && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 pl-4 border-l-2 border-gray-700/50"
                          >
                            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                              <h5 className="text-sm font-semibold text-white mb-3">
                                Add-ons for Guest {guestNum}
                              </h5>
                              <div className="space-y-2">
                                {/* Active Add-ons */}
                                {activeAddOns.map((selectedAddOn, idx) => {
                                  const addOn = getAddOnById(selectedAddOn.add_on_id);
                                  if (!addOn) return null;
                                  
                                  const hasValidImage = addOn.image_id && addOn.image_id !== '00000000-0000-0000-0000-000000000000';
                                  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.image_id, 'image').url : null;
                                  
                                  return (
                                    <div
                                      key={`active-${guestIndex}-${selectedAddOn.add_on_id}-${idx}`}
                                      className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg"
                                    >
                                      {imageUrl ? (
                                        <div
                                          className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-cover bg-center"
                                          style={{ backgroundImage: `url(${imageUrl})` }}
                                        />
                                      ) : (
                                        <div className="w-12 h-12 rounded-lg flex-shrink-0 bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
                                          <span className="text-white text-xs font-semibold">AO</span>
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-white truncate">{addOn.name}</p>
                                        <PriceDisplay price={selectedAddOn.price} />
                                      </div>
                                    </div>
                                  );
                                })}
                                
                                {/* Removed Add-ons */}
                                {removedAddOns.map((selectedAddOn, idx) => {
                                  const addOn = getAddOnById(selectedAddOn.add_on_id);
                                  if (!addOn) return null;
                                  
                                  const hasValidImage = addOn.image_id && addOn.image_id !== '00000000-0000-0000-0000-000000000000';
                                  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.image_id, 'image').url : null;
                                  
                                  return (
                                    <div
                                      key={`removed-${guestIndex}-${selectedAddOn.add_on_id}-${idx}`}
                                      className="flex items-center gap-3 p-2 bg-gray-800/30 rounded-lg border border-orange-500/30 opacity-60"
                                    >
                                      {imageUrl ? (
                                        <div
                                          className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-cover bg-center opacity-50"
                                          style={{ backgroundImage: `url(${imageUrl})` }}
                                        />
                                      ) : (
                                        <div className="w-12 h-12 rounded-lg flex-shrink-0 bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center opacity-50">
                                          <span className="text-white text-xs font-semibold">AO</span>
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-400 truncate line-through">{addOn.name}</p>
                                        <PriceDisplay price={selectedAddOn.price} />
                                      </div>
                                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded text-xs font-semibold border border-orange-500/30">
                                        Removed
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info */}
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-gray-300">
                  Click on the add-on count badge to view a guest's add-ons. Click the minus button to remove a guest. Changes will be saved when you click "Update".
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default EditGuestsModal;
